import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { LayoutDashboard, Menu, Bell, History, LogOut, Settings, Layers, CalendarPlus, ChevronLeft, Users, MapPin, ChevronDown, ChevronRight, ClipboardList, SearchCode, BellRing, CalendarClock, AlertTriangle, FileSpreadsheet, CalendarOff, MessageSquare, Clock, CalendarDays, CalendarSync } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocationContext } from '../context/LocationContext';
import { useRealtime } from '../realtime/RealtimeProvider';

/**
 * Main application layout with refined sidebar navigation.
 * Provides consistent structure for all admin pages.
 */
export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { user, logout, isSuperAdmin } = useAuth();
  const { status: liveStatus } = useRealtime();
  const { locations, selectedLocationId, selectLocation, currentLocation } = useLocationContext();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const [remindersOpen, setRemindersOpen] = useState(() => {
    return ['/booking-alerts', '/appointment-alerts', '/alert-logs', '/booking-analytics'].includes(routerLocation.pathname);
  });

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: CalendarPlus, label: 'Bookings', path: '/bookings' },
    { icon: CalendarSync, label: 'PPM Realignment', path: '/ppm-realignment' },
    {
      icon: Bell, label: 'Reminders', group: true, stateKey: 'reminders',
      children: [
        { icon: Bell, label: 'Booking Alerts', path: '/booking-alerts' },
        { icon: BellRing, label: 'Appointment Alerts', path: '/appointment-alerts' },
        { icon: MessageSquare, label: 'Alerts Logs', path: '/alert-logs' },
        { icon: History, label: 'Booking Analytics', path: '/booking-analytics' },
      ],
    },
    { icon: AlertTriangle, label: 'Unbooked Jobs', path: '/unbooked-jobs' },
    { icon: ClipboardList, label: 'Booking Logs', path: '/booking-logs' },
    { icon: CalendarClock, label: 'Slot Management', path: '/slot-management' },
    { icon: CalendarDays, label: 'Tech Calendar', path: '/technician-calendar' },
    { icon: AlertTriangle, label: 'Booking Conflicts', path: '/booking-conflict' },
    { icon: CalendarOff, label: 'Leave Management', path: '/leave-management' },
    { icon: SearchCode, label: 'Search Jobs', path: '/search-jobs' },
    { icon: Layers, label: 'AMC Settings', path: '/amc-settings' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  // Admin menu items (super_admin only)
  const adminItems = isSuperAdmin ? [
    { icon: Users, label: 'Users', path: '/admin/users' },
    { icon: MapPin, label: 'Locations', path: '/admin/locations' },
    { icon: History, label: 'Audit Log', path: '/admin/audit-log' },
  ] : [];

  // Get user initials for avatar
  const getInitials = () => {
    if (!user?.email) return '?';
    const parts = user.email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return user.email[0].toUpperCase();
  };

  const showLocationSelector = locations.length > 1;

  return (
    <div className="flex h-screen bg-[#f5f8f6] overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`bg-white border-r border-brand-light/80 flex flex-col transition-all duration-300 ease-in-out ${sidebarOpen ? 'w-60' : 'w-[68px]'
          } overflow-hidden relative`}
      >
        {/* Brand Header */}
        <div className="relative p-3 border-b border-brand-light/60 flex items-center justify-between h-16 bg-gradient-to-b from-white to-brand-light/20">
          <div
            className={`font-bold text-brand-dark flex items-center gap-3 ${!sidebarOpen && 'justify-center w-full'}`}
          >
            <img
              src="/logo.png"
              alt="The Golf Buggy Guy"
              className={`${sidebarOpen ? 'h-10' : 'h-8'} w-auto object-contain transition-all duration-300`}
            />
            {sidebarOpen && (
              <div className="leading-tight">
                <span className="text-sm font-bold text-brand-primary block tracking-tight">Golf Buggy</span>
                <span className="text-[11px] text-brand-neutral font-medium">AMC Manager</span>
              </div>
            )}
          </div>

          {/* Collapse toggle — sits with the brand so it is where the eye already is.
              When collapsed the brand takes the full width, so the button overlays
              the right edge instead of squeezing the logo. */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-lg text-brand-neutral hover:text-brand-primary hover:bg-brand-light/60 transition-all duration-200 ${
              sidebarOpen ? '' : 'absolute right-1 top-1/2 -translate-y-1/2 bg-white/90 shadow-sm border border-brand-light/60'
            }`}
          >
            <ChevronLeft className={`w-4 h-4 transition-transform duration-300 ${!sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Location Selector (shown only if user has multiple locations) */}
        {showLocationSelector && (
          <div className={`px-3 py-2 border-b border-brand-light/60 ${!sidebarOpen ? 'px-2' : ''}`}>
            {sidebarOpen ? (
              <div className="relative">
                <select
                  value={selectedLocationId || ''}
                  onChange={(e) => selectLocation(parseInt(e.target.value, 10))}
                  className="w-full appearance-none bg-brand-light/40 border border-brand-light rounded-lg px-3 py-2 pr-8 text-[12px] font-medium text-brand-dark cursor-pointer hover:bg-brand-light/70 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                >
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-neutral pointer-events-none" />
              </div>
            ) : (
              <button
                title={currentLocation?.name || 'Select location'}
                className="w-full flex items-center justify-center py-1.5"
              >
                <MapPin className="w-4 h-4 text-brand-primary" />
              </button>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-hide">
          {menuItems.map((item) => {
            if (item.group) {
              const Icon = item.icon;
              const isChildActive = item.children.some(c => routerLocation.pathname === c.path);
              const isOpen = (item.stateKey === 'reminders' ? remindersOpen : reportsOpen) || isChildActive;
              const setOpen = item.stateKey === 'reminders' ? setRemindersOpen : setReportsOpen;

              return (
                <div key={item.label}>
                  <button
                    onClick={() => sidebarOpen ? setOpen(!isOpen) : navigate(item.children[0].path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 group relative ${
                      isChildActive && !sidebarOpen
                        ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-md shadow-brand-primary/20'
                        : isChildActive
                        ? 'text-brand-primary'
                        : 'text-brand-dark/70 hover:bg-brand-light/60 hover:text-brand-primary'
                    } ${!sidebarOpen && 'justify-center'}`}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                    {sidebarOpen && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                      </>
                    )}
                  </button>
                  {sidebarOpen && isOpen && (
                    <div className="ml-5 pl-3 border-l border-brand-light/60 mt-1 space-y-0.5">
                      {item.children.map((child) => {
                        const isActive = routerLocation.pathname === child.path;
                        const ChildIcon = child.icon;
                        return (
                          <button
                            key={child.path}
                            onClick={() => navigate(child.path)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all duration-200 ${
                              isActive
                                ? 'bg-brand-primary/10 text-brand-primary'
                                : 'text-brand-dark/60 hover:bg-brand-light/60 hover:text-brand-primary'
                            }`}
                          >
                            <ChildIcon className="w-4 h-4 flex-shrink-0" />
                            <span>{child.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = routerLocation.pathname === item.path;
            const Icon = item.icon;

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 group relative ${isActive
                    ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-md shadow-brand-primary/20'
                    : 'text-brand-dark/70 hover:bg-brand-light/60 hover:text-brand-primary'
                  } ${!sidebarOpen && 'justify-center'}`}
                title={!sidebarOpen ? item.label : undefined}
              >
                {/* Active indicator bar */}
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-white/60 rounded-r-full" />
                )}
                <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-transform duration-200 ${!isActive ? 'group-hover:scale-110' : ''
                  }`} />
                {sidebarOpen && <span>{item.label}</span>}
              </button>
            );
          })}

          {/* Admin section divider + items */}
          {adminItems.length > 0 && (
            <>
              {sidebarOpen && (
                <div className="pt-3 pb-1">
                  <p className="px-3 text-[10px] font-semibold text-brand-neutral/60 uppercase tracking-wider">Admin</p>
                </div>
              )}
              {!sidebarOpen && <div className="border-t border-brand-light/60 my-2" />}
              {adminItems.map((item) => {
                const isActive = routerLocation.pathname === item.path;
                const Icon = item.icon;

                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 group relative ${isActive
                        ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-md shadow-brand-primary/20'
                        : 'text-brand-dark/70 hover:bg-brand-light/60 hover:text-brand-primary'
                      } ${!sidebarOpen && 'justify-center'}`}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-white/60 rounded-r-full" />
                    )}
                    <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-transform duration-200 ${!isActive ? 'group-hover:scale-110' : ''
                      }`} />
                    {sidebarOpen && <span>{item.label}</span>}
                  </button>
                );
              })}
            </>
          )}
        </nav>

        {/* Footer - User & Controls */}
        <div className="p-3 border-t border-brand-light/60 space-y-2 bg-gradient-to-b from-transparent to-brand-light/10">
          {/* User Info */}
          {sidebarOpen && user && (
            <div className="flex items-center gap-2.5 px-2 py-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
                {getInitials()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-brand-dark font-medium truncate">{user.email}</p>
                <p className="text-[10px] text-brand-neutral flex items-center gap-1">
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full ${liveStatus === 'live' ? 'bg-emerald-500' : liveStatus === 'offline' ? 'bg-brand-neutral/40' : 'bg-amber-400 animate-pulse'}`}
                    title={liveStatus === 'live' ? 'Live — this screen updates by itself' : liveStatus === 'offline' ? 'Not connected to live updates' : 'Reconnecting to live updates'}
                  />
                  {liveStatus === 'live' ? 'Live' : liveStatus === 'offline' ? 'Offline' : 'Reconnecting'}
                </p>
              </div>
            </div>
          )}
          {!sidebarOpen && user && (
            <div className="flex justify-center py-1">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center text-white text-xs font-bold shadow-sm">
                {getInitials()}
              </div>
            </div>
          )}

          {/* Logout */}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] text-brand-neutral hover:text-red-600 hover:bg-red-50/80 transition-all duration-200 ${!sidebarOpen && 'justify-center'}`}
            title={!sidebarOpen ? 'Sign Out' : undefined}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {children}
      </div>
    </div>
  );
}
